/**
 * `2n` — Clientes (tenants).
 *
 * 🔒 **BR-26 é o que a engrenagem significa.** Configuração POR CLIENTE mora no
 * MÓDULO, não em Administração: a engrenagem ao lado de um módulo habilitado
 * leva à configuração daquele módulo para aquele cliente. Ela é um link, e não
 * um modal, exatamente para que a configuração continue morando lá.
 *
 * 🔒 **BR-11 — os toggles são feature flags por tenant.** Desligar um módulo aqui
 * é o que faz ele sumir da sidebar daquele cliente.
 *
 * ⚠️ **`novo cliente` usa SÓ as colunas que já existem em `administracao.clientes`**
 * — nome (o `idCliente` é derivado dele pela Lambda) e módulos habilitados. Os
 * campos do formulário seguem sendo o item 7 da lista `EM ABERTO` do handoff; o
 * Fábio decidiu em 17/09/2026 não esperar por eles e não inventar nenhum. Se a iOX
 * pedir campos, é migration + contrato + este formulário. O botão mora na barra do
 * módulo (`AdminFrame`) e abre o formulário por `?novo=1`.
 *
 * ⚠️ **O dia de início do mês fiscal saiu do formulário em 15/10/2026** (pedido do
 * Fábio). A coluna `dia_fiscal_inicio` continua no banco, mas é só o fallback: o
 * dia que vale é o do contrato em Orçamento (V0013, BR-21). O cliente nasce com o
 * DEFAULT 1 — a Lambda usa 1 quando o corpo não traz `diaFiscalInicio`.
 *
 * ⚠️ **A contagem de usuários é derivada, não é um campo.** `Cliente` não carrega
 * `quantidadeUsuarios` no §7.5, então a coluna conta quantos usuários listam este
 * cliente. As duas rotas já existiam; a junção não inventa contrato nenhum.
 */
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import type { ClienteCriado } from '@/api/contracts/administracao'
import {
  useAlternarModuloDoCliente,
  useClientesAdm,
  useCriarCliente,
  useUsuariosAdm,
} from '@/api/queries/administracao'
import { MODULOS } from '@/app/navigation'
import { EsqueletoDeTabela } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Botao, Campo, Entrada, Switch } from '@/components/primitivos'
import { usePermission } from '@/permissions/usePermission'

/**
 * Onde a engrenagem de cada módulo leva.
 *
 * ⚠️ **Compliance não aparece aqui de propósito.** O ciclo é GLOBAL e
 * Administrador-only (BR-23) — não existe "configuração de compliance deste
 * cliente" para uma engrenagem abrir. Uma engrenagem que abrisse a gestão do
 * ciclo estaria sugerindo que ela é por tenant, que é o oposto da regra.
 */
const CONFIGURACAO_DO_MODULO: Record<string, string> = {
  finops: 'finops/configuracoes',
  // A configuração por cliente do Orçamento É a tela de contratos (BR-26).
  orcamento: 'orcamento/contratos',
}

export function Clientes() {
  const { editar } = usePermission('admin.clientes')

  const consulta = useClientesAdm()
  const usuarios = useUsuariosAdm()
  const alternar = useAlternarModuloDoCliente()

  const [params, setParams] = useSearchParams()
  const [criado, setCriado] = useState<ClienteCriado | null>(null)
  const formularioAberto = editar && params.get('novo') === '1'

  function fecharFormulario() {
    setParams(
      (atuais) => {
        const proximos = new URLSearchParams(atuais)
        proximos.delete('novo')
        return proximos
      },
      { replace: true },
    )
  }

  const formulario = formularioAberto && (
    <FormularioDeNovoCliente
      aoCancelar={fecharFormulario}
      aoCriar={(cliente) => {
        setCriado(cliente)
        fecharFormulario()
      }}
    />
  )

  const avisoDeCriado = criado && (
    <p role="status" className="text-success-700 type-sm">
      Cliente <strong>{criado.nome}</strong> criado (identificador <code>{criado.idCliente}</code>).
      Ele só aparece no seletor de cliente de quem for vinculado a ele em{' '}
      <Link to="/admin/usuarios" className="text-text-link hover:text-iox-teal underline">
        Usuários &amp; convites
      </Link>
      .
    </p>
  )

  if (consulta.isPending) {
    return <EsqueletoDeTabela rotulo="Carregando clientes…" colunas={3} linhas={4} />
  }
  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }
  if (consulta.data.length === 0) {
    // O formulário continua alcançável: sem ele, o primeiro tenant não teria como
    // nascer pela tela.
    return (
      <section className="flex flex-col gap-4">
        {formulario}
        {avisoDeCriado}
        <EstadoVazio titulo="Nenhum cliente cadastrado na plataforma." />
      </section>
    )
  }

  // Módulos escopados por cliente. Administração não é um tenant e não entra.
  const modulosDeCliente = MODULOS.filter((m) => m.escopadoPorCliente)

  return (
    <section className="flex flex-col gap-4">
      <h2 className="type-h4 text-text-primary">Clientes</h2>

      {formulario}
      {avisoDeCriado}

      <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-border-subtle border-b">
              <th scope="col" className="text-text-muted px-5 py-3 text-left type-eyebrow">
                Cliente (tenant)
              </th>
              <th scope="col" className="text-text-muted px-5 py-3 text-left type-eyebrow">
                Módulos (feature flags) — engrenagem abre a config. do módulo para o cliente
              </th>
              <th scope="col" className="text-text-muted px-5 py-3 text-left type-eyebrow">
                Usuários
              </th>
            </tr>
          </thead>
          <tbody>
            {consulta.data.map((cliente) => (
              <tr key={cliente.idCliente} className="border-border-subtle border-b last:border-b-0">
                <td className="px-5 py-4 align-top">
                  <span className="text-text-primary block type-body font-semibold">
                    {cliente.nome}
                  </span>
                  <span className="text-text-muted block type-sm">
                    dados segregados por cliente
                  </span>
                </td>

                <td className="px-5 py-4 align-top">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    {modulosDeCliente.map((modulo) => {
                      const habilitado = cliente.modulosHabilitados.includes(modulo.id)
                      const destino = CONFIGURACAO_DO_MODULO[modulo.id]
                      return (
                        <span key={modulo.id} className="flex items-center gap-2">
                          <Switch
                            ligado={habilitado}
                            desabilitado={!editar}
                            ocupado={alternar.isPending}
                            rotulo={`${modulo.rotulo} para ${cliente.nome}`}
                            aoAlternar={(proximo) =>
                              alternar.mutate({
                                idCliente: cliente.idCliente,
                                modulo: modulo.id,
                                habilitado: proximo,
                              })
                            }
                          />
                          <span
                            className={`type-sm ${
                              habilitado ? 'text-text-primary' : 'text-text-muted'
                            }`}
                          >
                            {modulo.rotulo}
                          </span>

                          {/* A engrenagem só existe se o módulo está LIGADO e se
                              ele tem configuração por cliente. Um link para a
                              config de um módulo desligado leva a uma tela que o
                              tenant não deveria ter. */}
                          {habilitado && destino && (
                            <Link
                              to={`/cliente/${cliente.idCliente}/${destino}`}
                              aria-label={`Configurações de ${modulo.rotulo} para ${cliente.nome}`}
                              title={`Configurações de ${modulo.rotulo}`}
                              className="text-icon-default hover:text-iox-teal duration-fast"
                            >
                              <i className="ph ph-gear-six" aria-hidden="true" />
                            </Link>
                          )}
                        </span>
                      )
                    })}
                  </div>
                </td>

                <td className="text-text-body px-5 py-4 align-top type-sm">
                  {usuarios.data
                    ? usuarios.data.filter((u) => u.clientes.includes(cliente.idCliente)).length
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {alternar.isError && <EstadoDeErro erro={alternar.error} />}

      <p className="text-text-muted type-sm">
        Listas globais de status de oportunidade e serviços AWS ficam na aba{' '}
        <strong className="text-text-body font-semibold">Configurações globais</strong>.
      </p>
    </section>
  )
}

/**
 * O formulário de novo cliente — controlado, sem biblioteca (CLAUDE.md, 20/08), no
 * mesmo desenho do convite da `2o`: campos, ação primária, erro do servidor abaixo.
 *
 * 🔒 Só nome e módulos. Não há campo de identificador: a Lambda o deriva
 * do nome e devolve 409 `CLIENTE_DUPLICADO` se ele já existir — a mensagem do
 * servidor aparece crua, porque é ela que diz qual identificador colidiu.
 */
function FormularioDeNovoCliente({
  aoCancelar,
  aoCriar,
}: {
  aoCancelar: () => void
  aoCriar: (cliente: ClienteCriado) => void
}) {
  const criar = useCriarCliente()
  const modulos = MODULOS.filter((m) => m.escopadoPorCliente)

  const [nome, setNome] = useState('')
  const [modulosHabilitados, setModulos] = useState<string[]>([])

  return (
    <form
      aria-label="Novo cliente"
      className="border-border-subtle bg-surface-card rounded-card flex flex-col gap-4 border p-5"
      onSubmit={(evento) => {
        evento.preventDefault()
        criar.mutate({ nome: nome.trim(), modulosHabilitados }, { onSuccess: aoCriar })
      }}
    >
      <h3 className="type-body-lg text-text-primary font-semibold">Novo cliente</h3>

      <div className="grid gap-4 md:grid-cols-2">
        <Campo
          rotulo="Nome do cliente"
          obrigatorio
          hint="o identificador do tenant é gerado a partir do nome"
        >
          <Entrada
            required
            placeholder="Tribunal de Justiça"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </Campo>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="type-label text-text-primary">Módulos habilitados</legend>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
          {modulos.map((modulo) => (
            <label key={modulo.id} className="flex cursor-pointer items-center gap-2 type-sm">
              <input
                type="checkbox"
                checked={modulosHabilitados.includes(modulo.id)}
                onChange={(e) =>
                  setModulos((atuais) =>
                    e.target.checked
                      ? [...atuais, modulo.id]
                      : atuais.filter((id) => id !== modulo.id),
                  )
                }
                className="accent-iox-navy h-4 w-4"
              />
              {modulo.rotulo}
            </label>
          ))}
        </div>
        <p className="text-text-muted type-sm">
          dá para ligar e desligar módulos depois, na tabela abaixo
        </p>
      </fieldset>

      <div className="flex flex-wrap items-center gap-3">
        <Botao variante="accent" type="submit" disabled={criar.isPending}>
          {criar.isPending ? 'criando…' : 'criar cliente'}
        </Botao>
        <Botao variante="discreta" onClick={aoCancelar}>
          cancelar
        </Botao>
      </div>

      {criar.isError && <EstadoDeErro erro={criar.error} />}
    </form>
  )
}
